import { QuestionCircleOutlined } from "@ant-design/icons";
import { Col, Divider, Form, Input, InputNumber, Row, Select, Tooltip, Typography } from "antd";
import React, { useEffect } from "react";
import { ElectricalConfiguration, ElectricalConfigurationInput, PhaseType } from "../../types/electricalConfig";
import {
  getFrequencyOptions,
  getPhaseTypeOptions,
  getSensorTypeOptions,
  getVoltageOptions,
} from "../../utils/electricalConfig";

const { Option } = Select;
const { Text } = Typography;

interface ElectricalConfigFormProps {
  initialValues?: ElectricalConfiguration;
  onValuesChange?: (changedValues: any, allValues: ElectricalConfigurationInput) => void;
  form?: any;
  disabled?: boolean;
  showAdvanced?: boolean;
  compact?: boolean;
  editMode?: boolean; // Add editMode prop to control validation behavior
}

const ElectricalConfigForm: React.FC<ElectricalConfigFormProps> = ({
  initialValues,
  onValuesChange,
  form,
  disabled = false,
  showAdvanced = true,
  compact = false,
  editMode = false, // Default to false for backward compatibility
}) => {
  const [formInstance] = Form.useForm(form);

  useEffect(() => {
    if (initialValues) {
      formInstance.setFieldsValue(initialValues);
    }
    // Don't automatically set defaults - let parent component provide proper initial values
    // This prevents overriding user's country-specific electrical settings with hardcoded European defaults
  }, [initialValues, formInstance]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePhaseTypeChange = (_phaseType: PhaseType) => {
    // Note: Voltage options are the same for all phase types since streams are measured individually
    // This handler is kept for future extensibility if needed
  };

  const basicFields = (
    <>
      <Row gutter={16}>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Voltage (V)
                <Tooltip title="Operating voltage of the electrical system">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="voltage"
            rules={[
              // Only require voltage in creation mode (not edit mode)
              ...(editMode ? [] : [{ required: true, message: "Voltage is required" }]),
              { type: "number", min: 100, max: 1000, message: "Voltage must be between 100V and 1000V" },
            ]}
          >
            <Select placeholder="Select voltage" disabled={disabled} showSearch allowClear>
              {getVoltageOptions().map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Frequency (Hz)
                <Tooltip title="Power system frequency (50Hz or 60Hz)">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="frequency"
            rules={[
              // Only require frequency in creation mode (not edit mode)
              ...(editMode ? [] : [{ required: true, message: "Frequency is required" }]),
            ]}
          >
            <Select placeholder="Select frequency" disabled={disabled}>
              {getFrequencyOptions().map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Calibration Factor
                <Tooltip title="Measurement calibration factor for accuracy adjustment">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="calibrationFactor"
            rules={[{ type: "number", min: 0.1, message: "Calibration factor must be greater than 0" }]}
          >
            <InputNumber placeholder="1.0" style={{ width: "100%" }} min={0.1} step={0.01} disabled={disabled} />
          </Form.Item>
        </Col>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Power Factor
                <Tooltip title="Ratio of real power to apparent power (0.0 - 1.0)">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="powerFactor"
            rules={[{ type: "number", min: 0, max: 1, message: "Power factor must be between 0.0 and 1.0" }]}
          >
            <InputNumber placeholder="0.85" style={{ width: "100%" }} min={0} max={1} step={0.01} disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const advancedFields = showAdvanced ? (
    <>
      <Divider>
        <Text type="secondary">Advanced Configuration</Text>
      </Divider>

      <Row gutter={16}>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Phase Type
                <Tooltip title="Single phase or three phase electrical system">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="phaseType"
            rules={[
              // Only require phase type in creation mode (not edit mode)
              ...(editMode ? [] : [{ required: true, message: "Phase type is required" }]),
            ]}
          >
            <Select placeholder="Select phase type" disabled={disabled} onChange={handlePhaseTypeChange}>
              {getPhaseTypeOptions().map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Nominal Current (A)
                <Tooltip title="Rated current capacity of the circuit">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="nominalCurrent"
            rules={[
              // Only require nominal current in creation mode (not edit mode)
              ...(editMode ? [] : [{ required: true, message: "Nominal current is required" }]),
              { type: "number", min: 0.1, message: "Nominal current must be greater than 0" },
            ]}
          >
            <InputNumber
              placeholder="Enter current"
              style={{ width: "100%" }}
              min={0.1}
              step={0.1}
              disabled={disabled}
              addonAfter="A"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Sensor Type
                <Tooltip title="Type of current sensing device">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="sensorType"
          >
            <Select placeholder="Select sensor type" disabled={disabled} allowClear>
              {getSensorTypeOptions().map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Primary (A)
                <Tooltip title="Primary current rating of the current transformer">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="primary"
          >
            <Input placeholder="100" disabled={disabled} addonAfter="A" />
          </Form.Item>
        </Col>
        <Col span={compact ? 24 : 12}>
          <Form.Item
            label={
              <span>
                Secondary (A)
                <Tooltip title="Secondary current rating of the current transformer">
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tooltip>
              </span>
            }
            name="secondary"
          >
            <Input placeholder="5" disabled={disabled} addonAfter="A" />
          </Form.Item>
        </Col>
      </Row>
    </>
  ) : null;

  return (
    <Form form={formInstance} layout="vertical" onValuesChange={onValuesChange} initialValues={initialValues}>
      {basicFields}
      {advancedFields}
    </Form>
  );
};

export default ElectricalConfigForm;
